import { ref, markRaw } from 'vue';

export class FilterDocumentsContext {
  constructor(duckDBService) {
    markRaw(this);
    this.duckDBService = markRaw(duckDBService);
    this.filterQueries = markRaw({});     // Store the actual queries
    this.filters = markRaw({});           // Visual metadata
    this.filtersLastUpdated = ref(new Date());
  }

  async initializeFilterContext() {
    const conn = this.duckDBService.conn;

    await conn.query(`
      DROP VIEW  IF EXISTS filteredDocuments;
      DROP TABLE IF EXISTS documents_filter_context;

      CREATE TEMPORARY TABLE documents_filter_context(Id VARCHAR);
      CREATE UNIQUE INDEX documents_filter_context_idx ON documents_filter_context(Id);

      INSERT INTO documents_filter_context
      SELECT DISTINCT Id FROM documents;

      CREATE VIEW filteredDocuments AS
      SELECT 
          d.Id AS id,
          d.SyncedDate AS syncedDate,
          d.CreatedDate AS createdDate,
          d.FileSize AS fileSize,
          d.ResourceId AS resourceId,
          d.StorageId AS storageId,
          d.FullPath AS fullPath,
          d.DisplayName AS displayName,
          d.ParentFolderId AS parentFolderId,
          d.FileDownloadLink AS fileDownloadLink,
          d.FileType AS fileType,
          d.CreatedByUserId AS createdByUserId,
          d.CreatedByUser AS createdByUser,
          d.CreatedByTeamId AS createdByTeamId,
          d.CreatedByTeam AS createdByTeam,
          d.Mime AS mime,
          d.IsFolder AS isFolder,
          d.IsHidden AS isHidden,
          d.ReceivedDate AS receivedDate
      FROM documents d
      INNER JOIN documents_filter_context fc ON d.Id = fc.Id;
    `);

    this.filters = markRaw({});
    this.filterQueries = markRaw({});
    this.filtersLastUpdated.value = new Date();
  }

  // New method: Update filter directly from SQL
  async updateFilterWithQuery(visualId, query) {
    const conn = this.duckDBService.conn;
    
    try {
      // Register query as a view for this filter
      await conn.query(`
        DROP VIEW IF EXISTS filter_view_${visualId};
        CREATE TEMPORARY VIEW filter_view_${visualId} AS (
          SELECT DISTINCT Id FROM (${query})
        );
      `);
      
      // Store the query reference
      this.filterQueries[visualId] = query;
      
      // Get count for metadata
      const countResult = await conn.query(`SELECT COUNT(*) as count FROM filter_view_${visualId}`);
      const count = countResult.toArray()[0].count;
      this.filters[visualId] = count;
      
      // Apply the combined filters
      await this.applyFiltersWithSQL();
      
    } catch (error) {
      console.error('Error updating filter with query:', error);
      // Clean up if needed
    }
  }

  // Apply all current filters using SQL intersection
  async applyFiltersWithSQL() {
    const conn = this.duckDBService.conn;
    const filterIds = Object.keys(this.filters);
    
    if (filterIds.length === 0) {
      await this.resetFilters();
      return;
    }
    
    try {
      // Use SQL INTERSECT to combine all filters
      if (filterIds.length === 1) {
        // Only one filter - simple replacement
        await conn.query(`
          DELETE FROM documents_filter_context;
          INSERT INTO documents_filter_context
          SELECT Id FROM filter_view_${filterIds[0]};
        `);
      } else {
        // Multiple filters - use SQL INTERSECT
        const intersectionQuery = filterIds
          .map(id => `SELECT Id FROM filter_view_${id}`)
          .join(' INTERSECT ');
        
        await conn.query(`
          DELETE FROM documents_filter_context;
          INSERT INTO documents_filter_context
          ${intersectionQuery};
        `);
      }
      
      this.filtersLastUpdated.value = new Date();
    } catch (error) {
      console.error('Error applying filters with SQL:', error);
      // Recover from error
      await this.resetFilters();
    }
  }

  // Legacy support for ID-based filtering
  async updateFilter(visualId, filteredIds) {
    const conn = this.duckDBService.conn;
    
    // Store for tracking
    this.filters[visualId] = filteredIds.length;
    
    if (filteredIds.length === 0) {
      // Empty filter case
      await conn.query(`
        DROP VIEW IF EXISTS filter_view_${visualId};
        CREATE TEMPORARY VIEW filter_view_${visualId} AS 
        SELECT CAST(NULL AS VARCHAR) AS Id WHERE 1=0;
      `);
    } else {
      // Create a temporary table and view
      const tempTable = `temp_filter_${visualId}`;
      
      await conn.query(`DROP TABLE IF EXISTS ${tempTable}`);
      await conn.query(`CREATE TEMPORARY TABLE ${tempTable}(Id VARCHAR)`);
      
      // Insert data in batches
      const batchSize = 200000;
      for (let i = 0; i < filteredIds.length; i += batchSize) {
        const chunk = filteredIds.slice(i, i + batchSize)
          .map(v => `('${v.replace(/'/g, "''")}')`);
        
        if (chunk.length > 0) {
          await conn.query(`INSERT INTO ${tempTable} VALUES ${chunk.join(',')}`);
        }
      }
      
      // Create a view from the table
      await conn.query(`
        DROP VIEW IF EXISTS filter_view_${visualId};
        CREATE TEMPORARY VIEW filter_view_${visualId} AS 
        SELECT DISTINCT Id FROM ${tempTable};
      `);
      
      // Clean up temp table
      await conn.query(`DROP TABLE ${tempTable}`);
    }
    
    // Apply combined filters
    await this.applyFiltersWithSQL();
  }

  async removeFilter(visualId) {
    if (!this.filters[visualId]) return;
    
    const conn = this.duckDBService.conn;
    
    // Remove filter view
    await conn.query(`DROP VIEW IF EXISTS filter_view_${visualId}`);
    
    // Remove from tracking
    delete this.filters[visualId];
    delete this.filterQueries[visualId];
    
    if (Object.keys(this.filters).length === 0) {
      await this.resetFilters();
    } else {
      await this.applyFiltersWithSQL();
    }
  }

  async resetFilters() {
    const conn = this.duckDBService.conn;
    
    await conn.query(`
      DELETE FROM documents_filter_context;
      INSERT INTO documents_filter_context
      SELECT DISTINCT Id FROM documents;
    `);
    
    this.filters = markRaw({});
    this.filterQueries = markRaw({});
    this.filtersLastUpdated.value = new Date();
  }

  async getFilteredIds() {
    const conn = this.duckDBService.conn;
    try {
      const res = await conn.query('SELECT Id FROM documents_filter_context');
      if (res?.getColumn) return res.getColumn('Id').toArray();
      if (Array.isArray(res)) return res.map(r => r.Id);
      if (res?.data) return res.data.map(r => r.Id);
      return [];
    } catch (error) {
      console.error('Error getting filtered IDs:', error);
      return [];
    }
  }

  // Adjusted executeFilteredQuery method for documents
  async executeFilteredQuery(sql) {
    const conn = this.duckDBService.conn;
    const ts = this.filtersLastUpdated.value.getTime();
    try {
      return await conn.query(`
        SELECT q.*
        FROM (${sql}) AS q
        INNER JOIN documents_filter_context fc ON q.Id = fc.Id
        /* ${ts} */
      `);
    } catch (error) {
      console.error('Error executing filtered query:', error);
      throw error; 
    }
  }

  getFilterDebugInfo() {
    return {
      activeFilters: Object.keys(this.filters).length,
      filtersByVisual: Object.entries(this.filters).map(([vid, count]) => ({
        visualId: vid,
        matchCount: count,
        hasQuery: !!this.filterQueries[vid]
      })),
      lastUpdated: this.filtersLastUpdated.value
    };
  }
}